import React, { FC, useEffect, useState } from "react";
import { ChatDots } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import CommonLayout from "./CommonLayout";
import { useAuth } from "components/AuthLogin/AuthContext";
import DeleteModal from "containers/BlogPage/BlogModal/DeleteModal";

interface Post {
  id: number;
  title: string;
  date: string;
  commentCount: number;
  featuredImage: string | null;
  categories: { name: string; color: string }[];
}

const AccountPosts: FC = () => {
  const { getCookie } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);

  useEffect(() => {
    const fetchMyPosts = async () => {
      const apiToken = getCookie("apiToken");
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/my-posts`, {
          headers: { Authorization: `Bearer ${apiToken}` },
        });
        const data = await res.json();
        setPosts(data);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchMyPosts();
  }, [getCookie]);

  const handleEditClick = async (postId: number) => {
    setEditLoadingId(postId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postId}`);
      if (!res.ok) throw new Error("Failed to fetch post");
      const fullPost = await res.json();
      navigate("/blog-modify", { state: { post: fullPost } });
    } catch (e) {
      console.error(e);
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId === null) return;
    const apiToken = getCookie("apiToken");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${deleteTargetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      if (res.ok) {
        setPosts((prev) => prev.filter((p) => p.id !== deleteTargetId));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteTargetId(null);
    }
  };

  return (
    <div className="nc-AccountPosts">
      <CommonLayout>
        <div className="space-y-6 sm:space-y-8">
          <h2 className="text-3xl font-semibold">내 글</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

          {loading ? (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700 animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center justify-between py-4 gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
                    <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-24" />
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <div className="h-7 w-12 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
                    <div className="h-7 w-14 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-red-500">글을 불러오지 못했어요. 다시 시도해 주세요.</div>
          ) : posts.length === 0 ? (
            <div className="text-neutral-500">아직 작성한 글이 없어요.</div>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {posts.map((post) => (
                <div key={post.id} className="flex items-center justify-between py-4 gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-sm font-medium truncate hover:text-primary-600 dark:hover:text-primary-400 text-left"
                        onClick={() => navigate(`/blog-single/${post.id}`)}
                      >
                        {post.title}
                      </button>
                      {post.commentCount > 0 && (
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 text-xs font-normal">
                          <ChatDots size={12} weight="thin" />
                          {post.commentCount}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-400 mt-1">{post.date}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditClick(post.id)}
                      disabled={editLoadingId === post.id}
                      className="text-xs px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50"
                    >
                      {editLoadingId === post.id ? "..." : "수정"}
                    </button>
                    <button
                      onClick={() => setDeleteTargetId(post.id)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CommonLayout>
      <DeleteModal
        isOpen={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        mainMessage="이 글을 삭제할까요?"
        subMessage="되돌릴 수 없어요."
      />
    </div>
  );
};

export default AccountPosts;
